import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Produit } from 'app/models/produit';
import { Observable, Subject, throwError } from 'rxjs';
import { tap, catchError, switchMap, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class ProduitsService {
  base_path = 'https://app.titv-store-api.com/';
  
  constructor(private http: HttpClient) { }

  private refreshData = new Subject<void>();
  
  get refreshNedeed() {
    return this.refreshData;
  }

  private getToken(): string {
    try {
      const authUser = sessionStorage.getItem('auth-user');
      if (authUser) {
        const userObj = JSON.parse(authUser);
        return userObj.token || '';
      }
      return '';
    } catch {
      return '';
    }
  }



  private getHeaders(): HttpHeaders {
    const token = this.getToken();
    if (token) {
      return new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + token
      });
    }
    return new HttpHeaders({
      'Content-Type': 'application/json'
    });
  }

  private handleError = (error: HttpErrorResponse) => {
    let errorMessage = 'Une erreur est survenue';
    if (error.status === 403) {
      errorMessage = 'Accès interdit. Veuillez vous reconnecter.';
    } else if (error.status === 401) {
      errorMessage = 'Non autorisé. Veuillez vous reconnecter.';
    } else {
      errorMessage = `Erreur ${error.status}: ${error.message}`;
    }
    console.error('Erreur ProduitsService:', errorMessage);
    return throwError(() => new Error(errorMessage));
  }
  
  createProduit(item: any): Observable<Produit> {
    const headers = this.getHeaders();
    return this.http.post<Produit>(this.base_path + 'produits/add', JSON.stringify(item), { headers })
      .pipe(tap(() => this.refreshData.next()), catchError(this.handleError));
  }

  getProduit(id: any): Observable<Produit> {
    const headers = this.getHeaders();
    return this.http.get<Produit>(this.base_path + 'produits/' + id, { headers })
      .pipe(catchError(this.handleError));
  }

  getListProduit(): Observable<Produit[]> {
    const headers = this.getHeaders();
    return this.http.get<Produit[]>(this.base_path + 'produits/all', { headers })
      .pipe(catchError(this.handleError));
  }

  // ✅ Full update (works)
  updateProduit(id: any, item: any): Observable<Produit> {
    const headers = this.getHeaders();
    return this.http.put<Produit>(this.base_path + 'produits/edit/' + id, JSON.stringify(item), { headers })
      .pipe(tap(() => this.refreshData.next()), catchError(this.handleError));
  }

  // ✅ Order update with fallback to full update
  updateProduitOrder(product: any): Observable<any> {
    const headers = this.getHeaders();
    const id = product.id;
    const displayOrder = product.displayOrder;
    
    // First try dedicated endpoint (object format, like projects)
    return this.http.put(
      this.base_path + 'produits/update-order/' + id,
      { displayOrder: displayOrder },
      { headers, responseType: 'text' }
    ).pipe(
      tap(() => this.refreshData.next()),
      catchError((error) => {
        // If 403 or 405, fallback to full update
        if (error.status === 403 || error.status === 405) {
          console.warn(`Order endpoint failed (${error.status}), falling back to full update for product ${id}`);
          // Remove displayOrder from the product object to avoid confusion? Keep as is.
          return this.updateProduit(id, product).pipe(
            tap(() => this.refreshData.next())
          );
        }
        return this.handleError(error);
      })
    );
  }

deleteProduit(id: any): Observable<any> {
  const headers = this.getHeaders();
  return this.http.delete(this.base_path + 'produits/delete/' + id, {
    headers,
    responseType: 'text'
  }).pipe(
    tap(() => this.refreshData.next()),
    map(response => ({ success: true, message: response })),
    catchError((error: HttpErrorResponse) => {
      let errorMsg = 'Erreur lors de la suppression du produit';
      if (error.status === 404) {
        errorMsg = 'Produit non trouvé';
      } else if (error.status === 500) {
        errorMsg = 'Erreur serveur';
      }
      return throwError(() => new Error(errorMsg));
    })
  );
}
  // produits.service.ts (add this method)
// produits.service.ts
updateProduitOrdersBatch(updates: { id: number; displayOrder: number }[]): Observable<any> {
  const headers = this.getHeaders();
  return this.http.put(this.base_path + 'produits/update-orders', updates, {
    headers,
    responseType: 'text'   // 👈 Add this line
  }).pipe(
    tap(() => this.refreshData.next()),
    catchError(this.handleError)
  );
}
}